import { useState, useEffect } from "react";
import { Check, Loader2, Lock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { bookOffer, createCheckout } from "@/lib/api";
import { euro } from "@/lib/format";

export const BookingModal = ({ open, onOpenChange, property, checkIn, checkOut, guests, childrenAges = "" }) => {
  const { t, config, lang } = useApp();
  const [loadingRate, setLoadingRate] = useState(true);
  const [rate, setRate] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !property) return;
    setRate(null);
    setLoadingRate(true);
    bookOffer(property.slug, { check_in: checkIn, check_out: checkOut, guests, children_ages: childrenAges })
      .then((r) => setRate(r))
      .catch(() => setRate(null))
      .finally(() => setLoadingRate(false));
  }, [open, property, checkIn, checkOut, guests, childrenAges]);

  if (!property) return null;

  const childCount = childrenAges ? childrenAges.split(",").filter((a) => a !== "").length : 0;
  const perNight = rate?.per_night ?? property.best_price;
  const nights = rate?.nights ?? property.nights ?? 1;
  const total = rate?.total ?? Math.round(perNight * nights);

  const submit = async () => {
    if (!name || !email) return;
    setSubmitting(true);
    try {
      const res = await createCheckout({
        slug: property.slug,
        guest_name: name,
        email,
        phone: phone || undefined,
        check_in: checkIn,
        check_out: checkOut,
        guests,
        children_ages: childrenAges || undefined,
        origin_url: window.location.origin,
        lang,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error("Error");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-sand-card sm:max-w-md" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-petrol">{t("detail.book_title")}</DialogTitle>
        </DialogHeader>
        <div className="rounded-xl border border-sand-border bg-white p-4">
          <p className="font-serif text-lg font-semibold text-petrol">{property.name}</p>
          <p className="text-sm text-petrol/60">{property.zone_name}</p>
          {checkIn && checkOut ? (
            <p className="mt-1 text-sm text-petrol/70">
              {checkIn} → {checkOut} · {guests} {t("search.guests")}{childCount ? ` · ${childCount} ${childCount === 1 ? t("search.child_one") : t("search.children_many")}` : ""}
            </p>
          ) : (
            <p className="mt-1 text-xs italic text-petrol/50">{t("detail.no_dates_note")}</p>
          )}
          <div className="mt-3 flex items-center justify-between">
            {loadingRate ? (
              <span className="flex items-center gap-2 text-sm text-petrol/60">
                <Loader2 size={15} className="animate-spin" /> {t("detail.checking_rate")}
              </span>
            ) : rate?.live_rate_confirmed ? (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                <Check size={14} /> {t("detail.rate_confirmed")}
              </span>
            ) : checkIn && checkOut ? (
              <span className="text-xs italic text-petrol/50">{t("detail.best_price_note")}</span>
            ) : (
              <span className="text-xs italic text-petrol/50">{t("detail.no_dates_note")}</span>
            )}
            <div className="text-right">
              <span className="font-serif text-2xl font-bold text-petrol">{euro(total)}</span>
              {nights > 1 && <span className="block text-xs text-petrol/50">{euro(perNight)}{t("card.per_night")} × {nights}</span>}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <input
            data-testid="booking-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("detail.guest_name")}
            className="rounded-lg border border-sand-border bg-white px-3 py-2.5 text-sm outline-none focus:border-terracotta"
          />
          <input
            data-testid="booking-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("detail.your_email")}
            className="rounded-lg border border-sand-border bg-white px-3 py-2.5 text-sm outline-none focus:border-terracotta"
          />
          {config?.whatsapp_enabled && (
            <div>
              <input
                data-testid="booking-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t("detail.whatsapp_phone")}
                className="w-full rounded-lg border border-sand-border bg-white px-3 py-2.5 text-sm outline-none focus:border-terracotta"
              />
              <p className="mt-1 flex items-center gap-1.5 text-xs text-emerald-600">
                <MessageCircle size={12} /> {t("detail.whatsapp_note")}
              </p>
              {config?.whatsapp?.sandbox && (
                <div
                  data-testid="whatsapp-sandbox-note"
                  className="mt-2 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800"
                >
                  {t("detail.whatsapp_sandbox_note")}
                  {config.whatsapp.join_code && (
                    <span className="mt-1 block font-medium">
                      {t("detail.whatsapp_sandbox_join")
                        .replace("{code}", config.whatsapp.join_code)
                        .replace("{num}", config.whatsapp.number)}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          <button
            data-testid="booking-confirm"
            onClick={submit}
            disabled={submitting || !name || !email}
            className="flex items-center justify-center gap-2 rounded-none bg-terracotta py-3 font-semibold text-white transition-colors hover:bg-terracotta-hover disabled:opacity-50"
          >
            {submitting ? <Loader2 size={17} className="animate-spin" /> : <Lock size={15} />}
            {submitting ? t("detail.confirming") : `${t("detail.confirm_booking")} · ${euro(total)}`}
          </button>
          <p className="text-center text-xs text-petrol/40">{t("detail.pay_secure")}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

